'use client';

import { syncOpenAiConsent, trackOpenAiEvent } from '@/lib/marketing/openai-browser';
import { browserMaySendGa4Event } from '@/lib/marketing/purchase-tracking';
import { openAiClickReference, openAiBrowserReference } from '@/lib/marketing/openai-ads';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { fetchSiteSettings } from '@/lib/client/site-settings-cache';
import { buildEnhancedUserData, hasCustomerIdentifiers, withoutCustomerData } from '@/lib/marketing/enhanced-conversions';
import { getConsentSnapshot, type ConsentPreferences } from '@/lib/marketing/consent';
import {
  attributionAllowedByConsent,
  captureGaAttributionSnapshot,
  readGaCookieIdentifiers,
} from '@/lib/google-analytics/browser-identifiers';
import { getConsentAwareFunnelSessionId } from '@/lib/marketing/funnel-session';
import { runtimeErrorContext, safeErrorContext } from '@/lib/monitoring/error-context';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    printenTrack?: (eventName: string, payload?: Record<string, unknown>) => void;
    printenMarketingSettings?: Record<string, any>;
    printenSiteProfile?: Record<string, any>;
    ttq?: Record<string, any>;
  }
}

function getConsent(): ConsentPreferences {
  if (typeof window === 'undefined') return getConsentSnapshot();
  return getConsentSnapshot(window.localStorage);
}

function safeLocalStorageGet(key: string) {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be blocked in some mobile/privacy contexts.
  }
}

function safeSessionStorageGet(key: string) {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionStorageSet(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage can be blocked in some mobile/privacy contexts.
  }
}

function normalizeCurrency(value: unknown) {
  const currency = String(value || 'EUR').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : 'EUR';
}

/**
 * Google Ads' cart-data conversion schema needs product id, quantity and
 * per-item price.  Printen's checkout already supplies those as `contents`
 * for Meta, so derive the Google representation from the same source of
 * truth instead of attempting to reconstruct an order from the page.
 */
function googleCartItems(contents: unknown) {
  if (!Array.isArray(contents)) return [];

  return contents.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const item = entry as Record<string, unknown>;
    const id = String(item.id || '').trim();
    const price = Number(item.item_price ?? item.price);
    const quantity = Number(item.quantity ?? 1);

    if (!id || !Number.isFinite(price) || price < 0 || !Number.isFinite(quantity) || quantity <= 0) {
      return [];
    }

    return [{
      id,
      price: Number(price.toFixed(2)),
      quantity: Math.max(1, Math.round(quantity)),
    }];
  });
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }
  return window.gtag;
}

/**
 * Decide at runtime whether third-party tracking vendors may be loaded.
 *
 * Do not use NEXT_PUBLIC_APP_ENV for this gate. Next.js replaces public env
 * variables while compiling the browser bundle; a missing build variable then
 * lets the optimizer remove the complete GA/Ads loader from production. Vercel
 * preview deployments are explicitly excluded so they cannot pollute reports.
 */
export function isLiveTrackingHostname(hostname: string) {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, '');
  if (!normalized) return false;
  if (normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1') return false;
  if (normalized.endsWith('.localhost') || normalized.endsWith('.vercel.app')) return false;
  return true;
}

const INTERNAL_FLAG = 'printen_internal_visitor';

/**
 * Eigen team niet tracken en niet in advertentiedoelgroepen laten komen: wie ooit in de admin
 * was ingelogd, of eenmalig ?intern=1 op de site opent (bijv. op de telefoon), wordt blijvend als
 * intern gemarkeerd. ?intern=0 zet het terug. Zonder deze markering kwam het team via de
 * remarketing-tag in de Google Ads-doelgroepen en zag het dagelijks de eigen advertenties.
 */
function isInternalVisitor() {
  try {
    const param = new URLSearchParams(window.location.search).get('intern');
    if (param === '1') window.localStorage.setItem(INTERNAL_FLAG, '1');
    if (param === '0') window.localStorage.removeItem(INTERNAL_FLAG);
    if (window.localStorage.getItem('admin_token')) window.localStorage.setItem(INTERNAL_FLAG, '1');
    return window.localStorage.getItem(INTERNAL_FLAG) === '1';
  } catch {
    return false;
  }
}

function mayLoadTrackingVendors() {
  return typeof window !== 'undefined' && isLiveTrackingHostname(window.location.hostname) && !isInternalVisitor();
}

function syncGoogleConsent(consent = getConsent()) {
  const gtag = ensureGtag();
  const signals = {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  } as const;
  gtag('consent', 'update', signals);
  // Deliberately log only consent states. This is useful in local QA and
  // cannot expose URLs, click IDs, orders or customer fields.
  if (process.env.NODE_ENV !== 'production') console.info('[printen consent]', signals);
}

// Analytics-scripts pas laden als de pagina klaar is: gtag/dataLayer bufferen
// alle aanroepen tot dan, dus er gaat niets verloren, maar de productpagina is
// eerder bruikbaar op mobiel.
const pendingScriptKeys = new Set<string>();
function appendWhenIdle(script: HTMLScriptElement) {
  if (pendingScriptKeys.has(script.src)) return;
  pendingScriptKeys.add(script.src);
  const append = () => {
    const run = () => { document.head.appendChild(script); pendingScriptKeys.delete(script.src); };
    const idle = (window as any).requestIdleCallback as ((cb: () => void, opts?: { timeout: number }) => number) | undefined;
    if (idle) idle(run, { timeout: 3000 }); else window.setTimeout(run, 1500);
  };
  if (document.readyState === 'complete') append();
  else window.addEventListener('load', append, { once: true });
}

function komtVanBetaalpagina() {
  try {
    const host = new URL(document.referrer).hostname;
    return /(^|\.)(stripe\.com|klarna\.com|mollie\.com)$/.test(host);
  } catch {
    return false;
  }
}

function loadGoogleTag(marketing: Record<string, any>, consent: ConsentPreferences) {
  if (!mayLoadTrackingVendors()) return;
  const ga4Id = String(marketing.ga4_measurement_id || '').trim();
  const googleAdsId = String(marketing.google_ads_id || '').trim();
  const loaderId = ga4Id || googleAdsId;
  if (!loaderId) return;

  const gtag = ensureGtag();
  syncGoogleConsent(consent);
  if (!document.querySelector(`script[data-printen-ga="${loaderId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(loaderId)}`;
    script.dataset.printenGa = loaderId;
    appendWhenIdle(script);
    gtag('js', new Date());
    // Terug van de betaalpagina (Stripe, Klarna) is geen nieuwe verwijzing: zonder ignore_referrer
    // telt GA4 de betaalprovider als bron en raakt de oorspronkelijke campagne de conversie kwijt.
    if (ga4Id) gtag('config', ga4Id, { send_page_view: false, ...(komtVanBetaalpagina() ? { ignore_referrer: 'true' } : {}) });
    // allow_enhanced_conversions: de gehashte user_data die we bij checkout,
    // aankoop, aanmelding en leads zetten, mag Ads gebruiken voor matching.
    if (googleAdsId) gtag('config', googleAdsId, { send_page_view: false, allow_enhanced_conversions: true });
  }
}

function loadGoogleTagManager(consent: ConsentPreferences) {
  if (!mayLoadTrackingVendors()) return;
  const gtmId = String(process.env.NEXT_PUBLIC_GTM_ID || '').trim();
  if (!gtmId || (!consent.analytics && !consent.marketing)) return;
  if (document.querySelector(`script[data-printen-gtm="${gtmId}"]`)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  script.dataset.printenGtm = gtmId;
  appendWhenIdle(script);
}

function loadMetaPixel(marketing: Record<string, any>, consent: ConsentPreferences) {
  if (!mayLoadTrackingVendors()) return;
  const pixelId = String(marketing.meta_pixel_id || '').trim();
  if (!pixelId || !consent.marketing) return;
  if (window.fbq || document.querySelector(`script[data-printen-meta="${pixelId}"]`)) return;

  const fbq = function (...args: unknown[]) {
    const queueFn = fbq as any;
    if (queueFn.callMethod) queueFn.callMethod(...args);
    else queueFn.queue.push(args);
  };
  const fbqAny = fbq as any;
  fbqAny.push = fbq;
  fbqAny.loaded = true;
  fbqAny.version = '2.0';
  fbqAny.queue = [];
  window.fbq = fbq;
  (window as any)._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.dataset.printenMeta = pixelId;
  document.head.appendChild(script);
  window.fbq('set', 'autoConfig', false, pixelId);
  window.fbq('init', pixelId);
}

/**
 * TikTok-pixel volgens TikToks eigen basiscode (ttq als wachtrij-array met
 * load/instance). Een eigen stub die alleen in ttq._q schreef, werd door
 * events.js niet uitgelezen. De pixel-ID komt uit de instellingen (admin),
 * met NEXT_PUBLIC_TIKTOK_PIXEL_ID alleen als fallback via /api/site-settings.
 * Paginaweergaven lopen via trackNow('page_view'), ook bij SPA-navigatie.
 */
function loadTikTokPixel(marketing: Record<string, any>, consent: ConsentPreferences) {
  if (!mayLoadTrackingVendors()) {
    window.ttq?.disableCookie?.();
    return;
  }
  const pixelId = String(marketing.tiktok_pixel_id || '').trim();
  if (!pixelId || !consent.marketing) {
    window.ttq?.disableCookie?.();
    return;
  }
  if (document.querySelector(`script[data-printen-tiktok="${pixelId}"]`) || (window.ttq as any)?._i?.[pixelId]) {
    window.ttq?.enableCookie?.();
    return;
  }

  const w = window as any;
  w.TiktokAnalyticsObject = 'ttq';
  const ttq = w.ttq = w.ttq || [];
  ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent'];
  ttq.setAndDefer = (target: any, method: string) => {
    target[method] = (...args: unknown[]) => {
      target.push([method, ...args]);
    };
  };
  ttq.methods.forEach((method: string) => ttq.setAndDefer(ttq, method));
  ttq.instance = (id: string) => {
    const instance = ttq._i[id] || [];
    ttq.methods.forEach((method: string) => ttq.setAndDefer(instance, method));
    return instance;
  };
  ttq.load = (id: string, options?: Record<string, unknown>) => {
    const src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = src;
    ttq._t = ttq._t || {};
    ttq._t[id] = Date.now();
    ttq._o = ttq._o || {};
    ttq._o[id] = options || {};
    const script = document.createElement('script');
    script.async = true;
    script.src = `${src}?sdkid=${encodeURIComponent(id)}&lib=ttq`;
    script.dataset.printenTiktok = id;
    appendWhenIdle(script);
  };
  ttq.load(pixelId);
  ttq.enableCookie();
}

/** TikTok-standaardevents; dezelfde mapping als de serverkant (tiktok-events.ts). */
const TIKTOK_BROWSER_EVENTS: Record<string, string> = {
  view_item: 'ViewContent',
  search: 'Search',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'CompletePayment',
  lead: 'SubmitForm',
  generate_lead: 'SubmitForm',
  newsletter_signup: 'Subscribe',
  subscribe: 'Subscribe',
  sign_up: 'CompleteRegistration',
};

function tiktokBrowserContents(payload: Record<string, unknown>) {
  if (Array.isArray(payload.contents)) {
    const contents = payload.contents.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') return [];
      const item = entry as Record<string, unknown>;
      const id = String(item.id || '').trim();
      if (!id) return [];
      return [{
        content_id: id,
        content_type: 'product',
        ...(typeof item.quantity === 'number' ? { quantity: item.quantity } : {}),
        ...(typeof item.item_price === 'number' ? { price: item.item_price } : {}),
      }];
    });
    if (contents.length) return contents;
  }
  if (Array.isArray(payload.contentIds)) {
    return payload.contentIds.filter((id): id is string => typeof id === 'string' && Boolean(id))
      .map((id) => ({ content_id: id, content_type: 'product' }));
  }
  return [];
}

function activateTrackingVendors(marketing: Record<string, any>, consent = getConsent()) {
  syncGoogleConsent(consent);
  syncOpenAiConsent(consent.marketing && !safeLocalStorageGet('admin_token'));
  loadGoogleTag(marketing, consent);
  loadGoogleTagManager(consent);
  loadMetaPixel(marketing, consent);
  loadTikTokPixel(marketing, consent);
}

function getStoredAttribution() {
  try {
    return JSON.parse(safeLocalStorageGet('printen_attribution') || '{}');
  } catch {
    return {};
  }
}

function getCookieValue(name: string) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))?.[1]
    ? decodeURIComponent(document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))?.[1] || '')
    : undefined;
}

function setTrackingCookie(name: string, value: string) {
  const maxAge = 60 * 60 * 24 * 90;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
}

// versFbclid: de fbclid komt uit de url van dit bezoek, niet uit opgeslagen
// attributie. Alleen dan mag hij een bestaand _fbc-cookie overschrijven.
function getMetaBrowserIds(fbclid?: string, versFbclid = false) {
  let fbp = getCookieValue('_fbp');
  const now = Date.now();
  if (!fbp) {
    fbp = `fb.1.${now}.${Math.floor(Math.random() * 10 ** 10)}`;
    setTrackingCookie('_fbp', fbp);
  }

  let fbc = getCookieValue('_fbc');
  // Een verse klik vervangt de oude. Voorheen bleef het eerste _fbc-cookie
  // staan, waardoor een tweede advertentieklik van dezelfde bezoeker aan de
  // vorige campagne werd toegeschreven, of aan een klik buiten het venster.
  if (fbclid && (!fbc || versFbclid)) {
    fbc = `fb.1.${now}.${fbclid}`;
    setTrackingCookie('_fbc', fbc);
  }

  return { fbp, fbc };
}

/**
 * A first-party, pseudonymous identifier used only after marketing consent.
 * It gives CAPI a stable matching key in privacy browsers where Meta cannot
 * persist _fbp itself, while never exposing an e-mail address or account ID.
 */
function getMetaExternalId() {
  const key = 'printen_meta_external_id';
  const stored = safeLocalStorageGet(key);
  if (/^meta_[a-z0-9_-]{16,128}$/i.test(stored || '')) return String(stored);

  const externalId = `meta_${uuid().replace(/[^a-z0-9_-]/gi, '')}`;
  safeLocalStorageSet(key, externalId);
  return externalId;
}

function getMetaTestEventCode() {
  const code = new URLSearchParams(window.location.search).get('meta_test_event_code');
  if (/^TEST\d+$/i.test(code || '')) {
    const normalized = String(code).toUpperCase();
    safeSessionStorageSet('printen_meta_test_event_code', normalized);
    return normalized;
  }

  const stored = safeSessionStorageGet('printen_meta_test_event_code');
  return /^TEST\d+$/i.test(stored || '') ? String(stored).toUpperCase() : undefined;
}

function getTikTokTestEventCode() {
  const code = new URLSearchParams(window.location.search).get('tiktok_test_event_code');
  if (/^TEST\d+$/i.test(code || '')) {
    const normalized = String(code).toUpperCase();
    safeSessionStorageSet('printen_tiktok_test_event_code', normalized);
    return normalized;
  }
  const stored = safeSessionStorageGet('printen_tiktok_test_event_code');
  return /^TEST\d+$/i.test(stored || '') ? String(stored).toUpperCase() : undefined;
}

function captureAttribution(consent = getConsent()): Record<string, unknown> {
  const params = new URLSearchParams(window.location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid', 'gbraid', 'wbraid'];
  const current: Record<string, string> = {};
  keys.forEach((key) => {
    const value = params.get(key);
    if (value) current[key] = value;
  });

  if (consent.marketing) {
    // TikTok-klik-ID en het _ttp-cookie van de pixel: alleen met
    // marketingtoestemming, en een verse klik vervangt de oude.
    const ttclid = params.get('ttclid');
    if (ttclid) current.ttclid = ttclid.slice(0, 500);
    const oppref = openAiClickReference(params.get('oppref')) || openAiClickReference(getCookieValue('__oppref'));
    if (oppref) current.oppref = oppref;
    const obref = openAiBrowserReference(getCookieValue('__obref'));
    if (obref) current.obref = obref;
  }
  const canStoreAttribution = consent.analytics || consent.marketing;
  if (!canStoreAttribution) {
    return {
      ...current,
      landing_page: window.location.href,
      referrer: document.referrer || undefined,
    };
  }

  const existing = attributionAllowedByConsent(getStoredAttribution(), consent);
  // Een verse Google-klik vervangt de vorige volledig. Zonder dit blijft een
  // oude gclid naast een nieuwe gbraid staan, en de serverupload kiest gclid
  // eerst. De aankoop wordt dan bij de verkeerde klik gemeld, of bij een klik
  // die allang buiten het terugkijkvenster valt.
  const googleClickIds = ['gclid', 'gbraid', 'wbraid'];
  if (googleClickIds.some((key) => current[key])) {
    googleClickIds.forEach((key) => {
      if (!current[key]) delete existing[key];
    });
  }
  const metaIds = consent.marketing
    ? {
      ...getMetaBrowserIds(current.fbclid || String(existing.fbclid || ''), Boolean(current.fbclid)),
      externalId: getMetaExternalId(),
    }
    : {};
  const ttp = consent.marketing ? getCookieValue('_ttp')?.slice(0, 500) : undefined;
  const tiktokIds: Record<string, string> = ttp ? { ttp } : {};
  const gaIdentifiers = consent.analytics ? readGaCookieIdentifiers(document.cookie, String(window.printenMarketingSettings?.ga4_measurement_id || '')) : {};

  // Een bestaande first-touch mag blijven staan, maar GA-identifiers ontstaan
  // pas nadat gtag geladen is. Daarom mogen we hier niet vroeg terugkeren:
  // iedere latere gebeurtenis krijgt de kans de client- en sessie-ID alsnog
  // aan de bewaarde attributie toe te voegen.
  if (
    Object.keys(current).length === 0
    && safeLocalStorageGet('printen_attribution')
    && (!gaIdentifiers.gaClientId || existing.gaClientId === gaIdentifiers.gaClientId)
    && (!gaIdentifiers.gaSessionId || existing.gaSessionId === gaIdentifiers.gaSessionId)
    && (!tiktokIds.ttp || existing.ttp === tiktokIds.ttp)
  ) {
    return existing;
  }
  const merged = {
    ...existing,
    ...current,
    ...metaIds,
    ...tiktokIds,
    ...gaIdentifiers,
    landing_page: existing.landing_page || window.location.href,
    referrer: existing.referrer || document.referrer || undefined,
    captured_at: existing.captured_at || new Date().toISOString(),
  };
  safeLocalStorageSet('printen_attribution', JSON.stringify(merged));
  return merged;
}

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `evt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Privacyvriendelijke basistelling voor bezoekers die optionele cookies niet
 * toestaan. Dit gebruikt geen cookie, sessie-ID, querystring, referrer,
 * klantgegevens of advertentie-ID. Iedere gebeurtenis staat volledig op
 * zichzelf; de server bewaart alleen het type, pad, taal en eventueel product.
 */
function sendPrivacySafeAggregate(
  eventName: string,
  payload: Record<string, unknown>,
  locale: string,
) {
  const productId = typeof payload.productId === 'string' ? payload.productId.slice(0, 160) : undefined;
  const productSku = typeof payload.productSku === 'string' ? payload.productSku.slice(0, 160) : undefined;
  void fetch('/api/marketing/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      eventName,
      eventId: `aggregate:${uuid()}`,
      privacyMode: 'aggregate',
      url: `${window.location.origin}${window.location.pathname}`,
      locale,
      ...(productId ? { productId } : {}),
      ...(productSku ? { productSku } : {}),
      consent: { analytics: false, marketing: false, preferences: false },
    }),
  }).catch(() => undefined);
}

const trackedEventIds = new Set<string>();

export function MarketingTracker() {
  const pathname = usePathname();
  const locale = useLocale();
  const [trackingReady, setTrackingReady] = useState(false);

  const trackPageView = useCallback(() => {
    const consent = getConsent();
    syncGoogleConsent(consent);
    window.printenTrack?.('page_view', {
      path: pathname,
      search: window.location.search.replace(/^\?/, ''),
      consent,
    });
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    let settingsReady = false;
    const pendingEvents: Array<[string, Record<string, unknown>]> = [];

    fetchSiteSettings()
      .then((data: any) => {
        if (cancelled) return;
        const marketing = data.marketing || {};
        const site = data.site || {};
        window.printenMarketingSettings = marketing;
        window.printenSiteProfile = site;

        activateTrackingVendors(marketing);
        // GA config en cookie-aanmaak zijn asynchroon. Verrijk de first-party
        // snapshot daarom ook via gtag's callback zodra de vendor is gestart.
        void captureGaAttributionSnapshot({
          getCurrentConsent: getConsent,
          consent: getConsent(),
          storage: window.localStorage,
          cookieHeader: document.cookie,
          gtag: window.gtag,
          measurementId: String(marketing.ga4_measurement_id || ''),
        });

        settingsReady = true;
        pendingEvents.splice(0).forEach(([eventName, payload]) => {
          window.printenTrack?.(eventName, payload);
        });
        setTrackingReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        settingsReady = true;
        pendingEvents.splice(0).forEach(([eventName, payload]) => {
          window.printenTrack?.(eventName, payload);
        });
        setTrackingReady(true);
      });

    const trackNow = (eventName: string, payload: Record<string, unknown> = {}) => {
      const consent = getConsent();
      const funnelSessionId = getConsentAwareFunnelSessionId(window.sessionStorage, consent);
      // Een beheerder die de live shop test mag nooit biedstrategieën,
      // retargetingdoelgroepen of ROAS-rapporten trainen. We versturen geen
      // token, alleen een interne markering naar onze eigen eventopslag.
      const internalTest = Boolean(safeLocalStorageGet('admin_token'));
      // Advanced Consent Mode: laad gtag ook bij denied consent en verstuur
      // uitsluitend een cookieless page-view ping. Google mag dan geen
      // analytics- of advertentiecookies schrijven, maar GA4/Ads ziet wel dat
      // de website verkeer ontving. First-party events, attributie-opslag en
      // Meta blijven geblokkeerd tot de bezoeker toestemming geeft.
      if (!consent.analytics && !consent.marketing) {
        if (eventName === 'page_view' && !internalTest) {
          const gtag = ensureGtag();
          syncGoogleConsent(consent);
          gtag('event', 'page_view', {
            page_location: window.location.href,
            page_path: `${window.location.pathname}${window.location.search}`,
            page_title: document.title,
          });
        }
        if (!internalTest) sendPrivacySafeAggregate(eventName, payload, locale);
        return;
      }
      const attribution = captureAttribution(consent);
      const site = window.printenSiteProfile || {};
      const metaIds = consent.marketing
        ? {
          ...getMetaBrowserIds(String(attribution.fbclid || '')),
          externalId: getMetaExternalId(),
        }
        : {};
      const eventId = String(payload.eventId || uuid());
      const eventKey = `${eventName}:${eventId}`;
      if (trackedEventIds.has(eventKey)) return;
      trackedEventIds.add(eventKey);
      const value = typeof payload.value === 'number' ? payload.value : undefined;
      const currency = normalizeCurrency(payload.currency);
      const normalizedPayload = { ...payload, ...(value !== undefined ? { value, currency } : {}) };

      const gtag = ensureGtag();
      const googleAdsId = window.printenMarketingSettings?.google_ads_id;
      const purchaseLabel = window.printenMarketingSettings?.google_ads_purchase_label;
      const beginCheckoutLabel = window.printenMarketingSettings?.google_ads_begin_checkout_label;
      const addToCartLabel = window.printenMarketingSettings?.google_ads_add_to_cart_label;

      // Alles wat naar Google (GA4 en Ads) gaat, in een vaste volgorde:
      // eerst de gehashte klantgegevens zetten, dan pas de events. Zo krijgen
      // zowel GA4 (user-provided data) als Ads (enhanced conversions) ze mee.
      // Klantgegevens gaan nooit leesbaar als eventparameter naar GA4.
      const sendToGoogle = (userData: Record<string, unknown> | null) => {
        if (userData) gtag('set', 'user_data', userData);

        if (consent.analytics && !internalTest && browserMaySendGa4Event(eventName)) {
          gtag('event', eventName, {
            event_id: eventId,
            site_id: site.id,
            site_domain: site.primaryDomain,
            ...withoutCustomerData(normalizedPayload),
          });
        }

        if (consent.marketing && !internalTest && eventName === 'purchase' && googleAdsId && purchaseLabel) {
          const adsValue = typeof payload.adsValue === 'number' ? payload.adsValue : value;
          const items = googleCartItems(payload.contents);
          gtag('event', 'conversion', {
            send_to: `${googleAdsId}/${purchaseLabel}`,
            value: adsValue,
            currency,
            transaction_id: payload.transaction_id || payload.orderNumber,
            discount: payload.discount,
            aw_merchant_id: payload.aw_merchant_id,
            aw_feed_country: payload.aw_feed_country,
            aw_feed_language: payload.aw_feed_language,
            ...(items.length > 0 ? { items } : {}),
          });
        }
        if (consent.marketing && !internalTest && eventName === 'begin_checkout' && googleAdsId && beginCheckoutLabel) {
          gtag('event', 'conversion', {
            send_to: `${googleAdsId}/${beginCheckoutLabel}`,
            value,
            currency,
          });
        }
        if (consent.marketing && !internalTest && eventName === 'add_to_cart' && googleAdsId && addToCartLabel) {
          gtag('event', 'conversion', {
            send_to: `${googleAdsId}/${addToCartLabel}`,
            value,
            currency,
          });
        }
      };

      // Gehashte klantgegevens alleen met advertentietoestemming (Consent
      // Mode ad_user_data). Het hashen kost een fractie van een milliseconde;
      // faalt het om welke reden dan ook, dan gaan de events alsnog weg.
      // Precies een keer versturen, nooit nul en nooit twee keer.
      if (consent.marketing && !internalTest && hasCustomerIdentifiers(payload)) {
        let alVerstuurd = false;
        const verstuurEenmaal = (userData: Record<string, unknown> | null) => {
          if (alVerstuurd) return;
          alVerstuurd = true;
          sendToGoogle(userData);
        };
        buildEnhancedUserData({
          email: payload.email,
          phone: payload.phone,
          firstName: payload.firstName,
          lastName: payload.lastName,
          city: payload.city,
          zip: payload.zip,
          country: payload.country,
        })
          .then(verstuurEenmaal)
          .catch(() => verstuurEenmaal(null));
      } else {
        sendToGoogle(null);
      }

      if (consent.marketing && !internalTest) trackOpenAiEvent(eventName, normalizedPayload, eventId);

      if (consent.marketing && !internalTest && window.fbq) {
        const metaEvent = eventName === 'page_view' ? 'PageView'
          : eventName === 'view_item' ? 'ViewContent'
          : eventName === 'add_to_cart' ? 'AddToCart'
          : eventName === 'begin_checkout' ? 'InitiateCheckout'
          : eventName === 'purchase' ? 'Purchase'
          : eventName === 'lead' || eventName === 'newsletter_signup' ? 'Lead'
          : eventName === 'design_saved' ? 'CustomizeProduct'
          : undefined;
        const metaParams: Record<string, unknown> = {};
        if (value !== undefined) {
          metaParams.value = value;
          metaParams.currency = currency || 'EUR';
        }
        if (payload.contentIds) metaParams.content_ids = payload.contentIds;
        // Zonder content_type kan Meta content_ids niet aan de catalogus
        // koppelen, en dan werkt geen enkele dynamische productadvertentie.
        // De serverkant stuurde dit al wel mee, de browser niet.
        if (payload.contentType) metaParams.content_type = payload.contentType;
        else if (payload.contentIds) metaParams.content_type = 'product';
        if (payload.contents) metaParams.contents = payload.contents;
        if (payload.orderNumber) metaParams.order_id = payload.orderNumber;
        if (metaEvent) window.fbq?.('track', metaEvent, metaParams, { eventID: eventId });
      }

      const ttq = window.ttq as any;
      if (consent.marketing && !internalTest && ttq && typeof ttq.track === 'function') {
        if (eventName === 'page_view') {
          ttq.page();
        } else if (TIKTOK_BROWSER_EVENTS[eventName]) {
          const contents = tiktokBrowserContents(payload);
          const tiktokParams: Record<string, unknown> = {};
          if (value !== undefined) {
            tiktokParams.value = value;
            tiktokParams.currency = currency || 'EUR';
          }
          if (contents.length) {
            tiktokParams.contents = contents;
            tiktokParams.content_type = 'product';
          }
          if (payload.orderNumber) tiktokParams.order_id = payload.orderNumber;
          // Zelfde event_id als de Events API-call via /api/marketing/event:
          // TikTok ontdubbelt browser en server daarop.
          ttq.track(TIKTOK_BROWSER_EVENTS[eventName], tiktokParams, { event_id: eventId });
        }
      }

      fetch('/api/marketing/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          eventName,
          eventId,
          tiktokTestEventCode: getTikTokTestEventCode(),
          url: window.location.href,
          referrer: document.referrer,
          locale,
          consent,
          siteId: site.id,
          siteDomain: site.primaryDomain,
          testEventCode: getMetaTestEventCode(),
          internalTest,
          ...metaIds,
          ...payload,
          ...(['checkout_error', 'checkout_blocked'].includes(eventName) ? {
            errorContext: { ...safeErrorContext({ code: payload.reason, durationMs: payload.durationMs }), ...runtimeErrorContext() },
          } : {}),
          attribution: {
            ...attribution,
            siteId: site.id,
            siteDomain: site.primaryDomain,
            ...(funnelSessionId ? { funnel_session_id: funnelSessionId } : {}),
          },
          ...(funnelSessionId ? { funnelSessionId } : {}),
        }),
      }).catch(() => undefined);
    };

    window.printenTrack = (eventName, payload = {}) => {
      if (!settingsReady) {
        pendingEvents.push([eventName, { ...payload, openAiConsentAtCapture: getConsent().marketing }]);
        if (pendingEvents.length > 50) pendingEvents.shift();
        return;
      }
      trackNow(eventName, payload);
    };
    window.dispatchEvent(new Event('printen-tracker-ready'));

    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    if (!trackingReady) return;
    trackPageView();
  }, [trackingReady, trackPageView]);

  useEffect(() => {
    if (!trackingReady) return;
    const handleConsentUpdate = () => {
      const consent = getConsent();
      const marketing = window.printenMarketingSettings || {};
      activateTrackingVendors(marketing, consent);
      void captureGaAttributionSnapshot({
          getCurrentConsent: getConsent,
        consent,
        storage: window.localStorage,
        cookieHeader: document.cookie,
        gtag: window.gtag,
        measurementId: String(marketing.ga4_measurement_id || ''),
      });
      trackPageView();
    };
    window.addEventListener('printen-consent-updated', handleConsentUpdate);
    return () => window.removeEventListener('printen-consent-updated', handleConsentUpdate);
  }, [trackingReady, trackPageView]);

  return null;
}
